import Select from 'antd/lib/select';
import * as React from 'react';
import { getSEGenericDropdownValues } from 'src/api/shipmentExecution';
import { KeyLabel } from 'src/Library/Types';

const inputType = {
  TASK_LIST: 'TASK_LIST',
  TEAM_LIST: 'TEAM_LIST',
  USER_LIST: 'USER_LIST',
}

const labels = {
  TASK_LIST: 'Task Name*',
  TEAM_LIST: 'Responsible Team Name*',
  USER_LIST: 'Responsible User Name*',
}

const UserAllocationFields = (props) => {
  const { field } = props || {};
  const { extraData } = field || {};
  const { values, shipmentExecutionId, sourceObjectId: taskId } = extraData || {};

  const taskField = values?.find(val => val.type === inputType.TASK_LIST);
  const teamField = values?.find(val => val.type === inputType.TEAM_LIST);
  const userField = values?.find(val => val.type === inputType.USER_LIST);

  const [fieldsToRender, setFieldsToRender] = React.useState([]);

  const [taskList, setTaskList] = React.useState<KeyLabel[]>();
  const [teamList, setTeamList] = React.useState<KeyLabel[]>();
  const [userList, setUserList] = React.useState<KeyLabel[]>();
  const [selectedTask, setSelectedTask] = React.useState<string>();
  const [selectedTeam, setSelectedTeam] = React.useState<string>();
  const [selectedUser, setSelectedUser] = React.useState<string>();

  const setInputOptions = (type, arr) => {
    switch (type) {
      case inputType.TASK_LIST: {
        setTaskList(arr);
        return;
      }
      case inputType.TEAM_LIST: {
        setTeamList(arr);
        return;
      }
      case inputType.USER_LIST: {
        setUserList(arr);
        return;
      }
    }
  }

  const loadData = async (type: keyof typeof inputType) => {
    if (!shipmentExecutionId) {
      return;
    }

    let apiParams: {
      fieldType: string;
      taskId: string;
      teamId?: string;
    } = {
      fieldType: type as string,
      taskId, 
    };

    if (type === inputType.USER_LIST) {
      apiParams = {
        fieldType: type as string,
        teamId: selectedTeam,
        taskId,
      };
    }

    const response = await getSEGenericDropdownValues({ type, shipmentExecutionId, apiParams });
    if (response.isSuccess) {
      setInputOptions(type, response.data);
    }
  }

  const getPlaceholder = (name: string): string => {
    if (!name) {
      return 'Enter value';
    }
    return `Enter ${name}`;
  }

  React.useEffect(() => {
    loadData('TEAM_LIST');
    loadData('TASK_LIST');
  }, [shipmentExecutionId]);

  React.useEffect(() => {
    setFieldsToRender(values?.map((val, index) => (
      renderInput(val.type, getPlaceholder(val.name), val.isReadOnly, index)
    )));
  }, [taskList, teamList, userList, selectedTeam, selectedUser, selectedTask]);

  React.useEffect(() => {
    if (!selectedTeam) {
      return;
    }
    loadData('USER_LIST');
  }, [selectedTeam]);

  React.useEffect(() => {
    /** prefill if user is present in newly fetched user list */
    let userKey = userList?.find(user => user.key === selectedUser)?.key;
    handleValueChange(userKey, 'USER_LIST');
  }, [userList]);

  const setInitialValues = (field) => {
    if (!field) {
      return;
    }

    const value = field.values?.[0];
    if (!value) {
      return;
    }

    field.type === inputType.TASK_LIST && setSelectedTask(value);
    field.type === inputType.TEAM_LIST && setSelectedTeam(value);
    field.type === inputType.USER_LIST && setSelectedUser(value);
  }

  const isInitialRender = React.useRef(true);

  React.useEffect(() => {
    if (!values?.length || !isInitialRender.current) {
      return;
    }
    values?.forEach(field => {
      setInitialValues(field);
    });
    isInitialRender.current = false;
  }, [values])

  React.useEffect(() => {
    setFieldValue();
  }, [selectedUser, selectedTask, selectedTeam]);

  const setFieldValue = () => {
    const newValues = [
      {
        ...taskField,
        values: selectedTask ? [selectedTask] : [],
      },
      {
        ...teamField,
        values: selectedTeam ? [selectedTeam] : [],
      },
      {
        ...userField,
        values: selectedUser ? [selectedUser] : [],
      },
    ].filter(val => val.fieldId);

    field?.setValue(newValues);
  }

  const handleValueChange = (val: string | undefined, type: keyof typeof inputType) => {
    if (type === inputType.TEAM_LIST) {
      setSelectedTeam(val);
    }
    if (type === inputType.TASK_LIST) {
      setSelectedTask(val);
    }
    if (type === inputType.USER_LIST) {
      setSelectedUser(val);
    }

    setFieldValue();
  }

  const getLabel = (type) => {
    return labels[type];
  }

  const getList = (type) => {
    let list: KeyLabel[] | undefined = [];
    switch (type) {
      case inputType.TASK_LIST: {
        return list = taskList;
      }
      case inputType.TEAM_LIST: {
        return list = teamList;
      }
      case inputType.USER_LIST: {
        return list = userList;
      }
      default: {
        return [];
      }
    }
  }

  const getValue = (type) => {
    switch (type) {
      case inputType.TASK_LIST: {
        return selectedTask;
      }
      case inputType.TEAM_LIST: {
        return selectedTeam;
      }
      case inputType.USER_LIST: {
        return selectedUser;
      }
      default: {
        return undefined;
      }
    }
  }

  const renderInput = (type, placeholder = '', isReadOnly = false, index?: number) => {
    const disabled = (index === 0) || isReadOnly || field?.disabled;
    return (
      <div style={{ width: '33%' }}>
        <div>{getLabel(type)}</div>
        <Select
          style={{ width: '90%' }}
          allowClear
          onChange={(e) => handleValueChange(e?.toString(), type)}
          placeholder={placeholder}
          value={getValue(type)}
          disabled={disabled}
        >
          {getList(type)?.map(item => (
            <Select.Option key={item.key}>
              {item.label}
            </Select.Option>
          ))}
        </Select>
      </div>
    )
  }

  return (
    <div style={{ display: 'flex' }}>
      {fieldsToRender}
    </div>
  );
}

export default UserAllocationFields;